import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Search, Plus, Edit, Trash2, CheckCircle, AlertCircle, Calendar,
  ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter,
  Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Clock, FileText,
  Shield, Tag, Users, Bell, Package, DollarSign,
  TrendingUp, AlertOctagon, Target, Settings, Hash
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const API_BASE = '/facturation/alertes-echeances';

function getDaysUntilDue(dueDate) {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function isAlertProche(alert) {
  if (alert.status !== 'actif' || !alert.due_date) return false;
  const days = getDaysUntilDue(alert.due_date);
  const threshold = parseInt(alert.threshold_days, 10) || 7;
  return days !== null && days >= 0 && days <= threshold;
}

function isAlertEnRetard(alert) {
  if (alert.status !== 'actif' || !alert.due_date) return false;
  const days = getDaysUntilDue(alert.due_date);
  return days !== null && days < 0;
}

const EMPTY_FORM = {
  alert_number: '',
  title: '',
  alert_type: '',
  document_type: '',
  due_date: '',
  threshold_days: '',
  priority: '',
  status: '',
  client_id: '',
  client_name: '',
  notes: '',
};

const AlertesEcheancesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.alertesEcheancesPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();

  const ALERT_TYPE_OPTIONS = useMemo(
    () => [
      { value: 'echeance', label: p('alert_type_echeance') },
      { value: 'stock', label: p('alert_type_stock') },
      { value: 'seuil', label: p('alert_type_seuil') },
      { value: 'paiement', label: p('alert_type_paiement') },
    ],
    [t],
  );

  const DOCUMENT_TYPE_OPTIONS = useMemo(
    () => [
      { value: 'facture', label: p('doc_type_facture') },
      { value: 'devis', label: p('doc_type_devis') },
      { value: 'commande', label: p('doc_type_commande') },
      { value: 'bon_livraison', label: p('doc_type_bon_livraison') },
      { value: 'avoir', label: p('doc_type_avoir') },
      { value: 'reglement', label: p('doc_type_reglement') },
    ],
    [t],
  );

  const PRIORITY_OPTIONS = useMemo(
    () => [
      { value: 'basse', label: p('priority_basse') },
      { value: 'normale', label: p('priority_normale') },
      { value: 'haute', label: p('priority_haute') },
      { value: 'critique', label: p('priority_critique') },
    ],
    [t],
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'actif', label: p('status_actif') },
      { value: 'inactif', label: p('status_inactif') },
      { value: 'resolu', label: p('status_resolu') },
    ],
    [t],
  );

  const alertTypeLabels = useMemo(
    () => ({
      echeance: p('alert_type_echeance'),
      stock: p('alert_type_stock'),
      seuil: p('alert_type_seuil'),
      paiement: p('alert_type_paiement'),
    }),
    [t],
  );

  const priorityLabels = useMemo(
    () => ({
      critique: p('priority_critique'),
      haute: p('priority_haute'),
      normale: p('priority_normale'),
      basse: p('priority_basse'),
    }),
    [t],
  );

  const statusLabels = useMemo(
    () => ({
      actif: p('status_actif'),
      inactif: p('status_inactif'),
      resolu: p('status_resolu'),
    }),
    [t],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [alertes, setAlertes] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);

  const [alertFormTab, setAlertFormTab] = useState('rule');

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_alertes');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'تنبيه الاستحقاق' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'تنبيه' });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);

  useEffect(() => {
    loadAlertes();
    loadClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAlertes = async () => {
    try {
      const response = await localApiAdapter.get(API_BASE);
      const rows = response?.data || response || [];
      setAlertes(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading alertes:', error);
      setAlertes([]);
    }
  };

  const loadClients = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/clients');
      setClients(response?.data || response || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const stats = useMemo(() => ({
    total: alertes.length,
    actives: alertes.filter((a) => a.status === 'actif').length,
    proches: alertes.filter((a) => isAlertProche(a)).length,
    enRetard: alertes.filter((a) => isAlertEnRetard(a)).length,
    resolues: alertes.filter((a) => a.status === 'resolu').length,
  }), [alertes]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('alert_number', (row) => row.alert_number),
    textColumnRule('title', (row) => row.title),
    exactColumnRule('alert_type', (row) => row.alert_type),
    textColumnRule('due_date', (row) => row.due_date),
    exactColumnRule('priority', (row) => row.priority),
    textColumnRule('client_name', (row) => row.client_name),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredAlertes = useMemo(() => {
    const base = alertes.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (dateRange.start || dateRange.end) {
        const dueDate = a.due_date ? new Date(a.due_date) : null;
        if (!dueDate) return false;
        if (dateRange.start) {
          const startOfDay = new Date(dateRange.start);
          startOfDay.setHours(0, 0, 0, 0);
          if (dueDate < startOfDay) return false;
        }
        if (dateRange.end) {
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          if (dueDate > endOfDay) return false;
        }
      }
      const q = searchTerm.toLowerCase();
      if (q && !(
        a.alert_number?.toLowerCase().includes(q) ||
        a.title?.toLowerCase().includes(q) ||
        a.client_name?.toLowerCase().includes(q)
      )) return false;
      if (quickFilter !== 'all' && a.alert_type !== quickFilter) return false;
      return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [alertes, statusFilter, dateRange, searchTerm, quickFilter, filters, columnFilterRules]);

  const alertesFooter = useMemo(() => {
    let proches = 0;
    let retards = 0;
    filteredAlertes.forEach((a) => {
      if (isAlertProche(a)) proches += 1;
      if (isAlertEnRetard(a)) retards += 1;
    });
    return { proches, retards };
  }, [filteredAlertes]);

  const statusOptions = useMemo(() => [
    { value: 'actif', label: p('status_actif'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'inactif', label: p('status_inactif'), icon: XCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'resolu', label: p('status_resolu'), icon: Shield, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  ], [p]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({
      value: String(c.id),
      label: c.company_name || c.name || String(c.id),
    })),
    [clients],
  );

  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredAlertes);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredAlertes.map((r) => r.id), [filteredAlertes]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingRecord(null);
    setShowModal(false);
    setAlertFormTab('rule');
  };

  const openNewAlertModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleClientChange = (clientId) => {
    const client = clients.find((c) => String(c.id) === String(clientId));
    setFormData({
      ...formData,
      client_id: clientId || '',
      client_name: client ? (client.company_name || client.name || '') : formData.client_name,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        threshold_days: formData.threshold_days !== '' ? parseInt(formData.threshold_days, 10) : 7,
      };
      if (editingRecord) {
        await localApiAdapter.put(`${API_BASE}/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(API_BASE, submitData);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      await loadAlertes();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('error_save', { defaultValue: 'تعذّر حفظ التنبيه' }));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      alert_number: record.alert_number || '',
      title: record.title || '',
      alert_type: record.alert_type || '',
      document_type: record.document_type || '',
      due_date: record.due_date || '',
      threshold_days: record.threshold_days != null ? String(record.threshold_days) : '',
      priority: record.priority || '',
      status: record.status || '',
      client_id: record.client_id || '',
      client_name: record.client_name || '',
      notes: record.notes || '',
    });
    setAlertFormTab('rule');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm', { defaultValue: 'هل تريد حذف هذا التنبيه؟' }))) {
      try {
        await localApiAdapter.delete(`${API_BASE}/${id}`);
        notifyDeleted(showNotify, entityLabel);
        await loadAlertes();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('delete_error', { defaultValue: 'تعذّر الحذف' }));
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`${API_BASE}/${id}`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadAlertes();
    } catch (error) {
      console.error('Error updating status:', error);
      notifyError(showNotify, p('error_save', { defaultValue: 'تعذّر التحديث' }));
    }
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedRecords([]);
    else setSelectedRecords(filteredRecordIds);
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)));
        setSelectedRecords([]);
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        loadAlertes();
      } catch (error) {
        console.error('Error bulk deleting:', error);
        notifyError(showNotify, p('notify_bulk_delete_error'));
      }
    }
  };

  const getExportData = () =>
    selectedRecords.length > 0
      ? alertes.filter((a) => selectedRecords.includes(a.id))
      : filteredAlertes;

  const handleExportCSV = () => {
    exportToCSV(getExportData(), `alertes_echeances_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), `alertes_echeances_${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportDropdown(false);
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(getExportData(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `alertes_echeances_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleImportCSV = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        const rows = Array.isArray(imported) ? imported : [imported];
        for (const item of rows) {
          await localApiAdapter.post(API_BASE, item);
        }
        notifyCreated(showNotify, entityLabel);
        loadAlertes();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        notifyError(showNotify, p('notify_import_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        const rows = Array.isArray(imported) ? imported : [imported];
        for (const item of rows) {
          await localApiAdapter.post(API_BASE, item);
        }
        notifyCreated(showNotify, entityLabel);
        loadAlertes();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        notifyError(showNotify, p('notify_import_error'));
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        const rows = Array.isArray(data) ? data : [data];
        for (const item of rows) {
          await localApiAdapter.post(API_BASE, item);
        }
        notifyCreated(showNotify, entityLabel);
        loadAlertes();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        notifyError(showNotify, p('notify_import_json_error'));
      }
    }, '.json');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString(dateLocale);
    } catch {
      return dateStr;
    }
  };

  const getAlertTypeLabel = (type) => {
    const opt = ALERT_TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? opt.label : type || '—';
  };

  const getDocumentTypeLabel = (type) => {
    const opt = DOCUMENT_TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? opt.label : type || '—';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      critique: 'bg-red-500/20 text-red-300 border-red-500/30',
      haute: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      normale: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      basse: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    };
    const labels = {
      critique: p('priority_critique'),
      haute: p('priority_haute'),
      normale: p('priority_normale'),
      basse: p('priority_basse'),
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[priority] || styles.normale}`}>
        {labels[priority] || priority || '—'}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      actif: 'bg-green-500/20 text-green-300 border-green-500/30',
      inactif: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      resolu: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    };
    const labels = {
      actif: p('status_actif'),
      inactif: p('status_inactif'),
      resolu: p('status_resolu'),
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.inactif}`}>
        {labels[status] || status || '—'}
      </span>
    );
  };

  const getDueBadge = (record) => {
    if (!record.due_date) return <span className="text-white/40 text-xs">—</span>;
    const days = getDaysUntilDue(record.due_date);
    if (record.status === 'resolu') {
      return <span className="px-3 py-1 rounded-full text-xs font-medium border bg-blue-500/20 text-blue-300 border-blue-500/30">{p('status_resolu')}</span>;
    }
    if (days < 0) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-red-500/20 text-red-300 border-red-500/30 flex items-center gap-1 w-fit">
          <AlertOctagon size={14} />{p('due_late_days', { days: Math.abs(days) })}
        </span>
      );
    }
    if (isAlertProche(record)) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-orange-500/20 text-orange-300 border-orange-500/30 flex items-center gap-1 w-fit">
          <Clock size={14} />{p('due_days_before', { days })}
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-medium border bg-green-500/20 text-green-300 border-green-500/30">
        {p('due_days_before', { days })}
      </span>
    );
  };

  const renderDatePickerHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
      <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} type="button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', color: 'rgba(255,255,255,0.8)' }}>
        <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
      </button>
      <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
        {date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}
      </div>
      <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} type="button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', color: 'rgba(255,255,255,0.8)' }}>
        <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
      </button>
    </div>
  );

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: Bell, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: p('stat_active'), value: stats.actives, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'near', title: p('stat_near'), value: stats.proches, icon: Clock, color: 'from-orange-500 to-orange-600' },
    { key: 'late', title: p('stat_late'), value: stats.enRetard, icon: AlertOctagon, color: 'from-red-500 to-red-600' },
    { key: 'resolved', title: p('stat_resolved'), value: stats.resolues, icon: Shield, color: 'from-purple-500 to-purple-600', className: 'max-lg:hidden' },
  ], [stats, p]);
  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    ...STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
  ], [p]);
  const qawaemStatusLabel = statusFilter === 'all' ? p('col_status') : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('col_status'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'alert_number', label: p('col_alert_number'), required: true },
    { key: 'title', label: p('col_title') },
    { key: 'alert_type', label: p('col_type') },
    { key: 'due_date', label: p('col_due_date') },
    { key: 'priority', label: p('col_priority') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 3 }),
    [qawaemBaseColumns, showCol],
  );

  const alertFormTabs = useMemo(() => ([
    { id: 'rule', label: p('section_regle'), icon: Bell, iconColor: 'text-blue-400' },
    { id: 'params', label: p('section_parametres'), icon: Settings, iconColor: 'text-purple-400' },
  ]), [p]);

  return (
    <div className="space-y-3 employees-container">
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />

<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-3"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={AlertTriangle}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openNewAlertModal}
      createLabel={p('new_alert')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
        <div className="space-y-6 pt-6" />
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
              />

              {selectedRecords.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium"
                >
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">{p('delete_count', { count: selectedRecords.length })}</span>
                </button>
              )}

              <div className="relative" ref={statusFilterRef}>
                <button
                  onClick={() => {
                    setIsStatusFilterOpen(!isStatusFilterOpen);
                    setIsDateRangeOpen(false);
                    setShowImportDropdown(false);
                    setShowExportDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{p('col_status')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      zIndex: 99999,
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                    }}
                  >
                    <div className="p-2">
                      {[{ value: 'all', label: p('all_statuses'), icon: Filter, color: 'text-white/50' }, ...STATUS_OPTIONS.map((s) => ({ ...s, icon: s.value === 'actif' ? CheckCircle : s.value === 'resolu' ? Shield : XCircle, color: s.value === 'actif' ? 'text-green-400' : s.value === 'resolu' ? 'text-blue-400' : 'text-gray-400' }))].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setStatusFilter(opt.value);
                            setIsStatusFilterOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${
                            statusFilter === opt.value ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'
                          }`}
                        >
                          <opt.icon size={16} className={opt.color} />
                          <span className="text-sm font-medium">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={dateRangeRef}>
                <button
                  onClick={() => {
                    setIsDateRangeOpen(!isDateRangeOpen);
                    setIsStatusFilterOpen(false);
                    setShowImportDropdown(false);
                    setShowExportDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{p('period')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      zIndex: 99999,
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      overflow: 'visible',
                    }}
                  >
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('start_date')}</label>
                          <DatePicker
                            selected={dateRange.start}
                            onChange={(date) => setDateRange({ ...dateRange, start: date })}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={p('select')}
                            locale={datePickerLocale}
                            renderCustomHeader={renderDatePickerHeader}
                            className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 gps-datepicker"
                            calendarClassName="gps-calendar"
                            wrapperClassName="w-full"
                            portalId="qawaem-datepicker-portal"
                            popperClassName="date-picker-popper-high-z"
                          />
                        </div>
                        <div>
                          <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('end_date')}</label>
                          <DatePicker
                            selected={dateRange.end}
                            onChange={(date) => setDateRange({ ...dateRange, end: date })}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={p('select')}
                            locale={datePickerLocale}
                            renderCustomHeader={renderDatePickerHeader}
                            className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 gps-datepicker"
                            calendarClassName="gps-calendar"
                            wrapperClassName="w-full"
                            portalId="qawaem-datepicker-portal"
                            popperClassName="date-picker-popper-high-z"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-3 mt-3 border-t border-white/10">
                        <button
                          onClick={() => {
                            setDateRange({ start: null, end: null });
                            setIsDateRangeOpen(false);
                          }}
                          className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl text-white/80 text-sm font-medium transition-all"
                        >
                          {p('reset')}
                        </button>
                        <button
                          onClick={() => setIsDateRangeOpen(false)}
                          className="flex-1 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-blue-300 text-sm font-medium transition-all"
                        >
                          {p('apply')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button
                  onClick={() => {
                    setShowImportDropdown(!showImportDropdown);
                    setShowExportDropdown(false);
                  }}
                  className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Upload size={16} />
                  <span className="text-sm font-medium">{p('import_label')}</span>
                  <ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      zIndex: 100002,
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                    }}
                  >
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span>
                      </button>
                      <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span>
                      </button>
                      <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button
                  onClick={() => {
                    setShowExportDropdown(!showExportDropdown);
                    setShowImportDropdown(false);
                  }}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Download size={16} />
                  <span className="text-sm font-medium">{p('export_label')}</span>
                  <ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      zIndex: 100002,
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                    }}
                  >
                    <div className="p-2">
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span>
                      </button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span>
                      </button>
                      <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('alert_number') ? <TableColumnHeaderFilter columnKey="alert_number" label={p('col_alert_number')} value={filters.alert_number || ''} onChange={(v) => setFilter('alert_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('title') ? <TableColumnHeaderFilter columnKey="title" label={p('col_title')} value={filters.title || ''} onChange={(v) => setFilter('title', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('alert_type') ? <TableColumnHeaderFilter columnKey="alert_type" label={p('col_type')} kind="select" options={ALERT_TYPE_OPTIONS} value={filters.alert_type || ''} onChange={(v) => setFilter('alert_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_document')}</th>
                  {showCol('due_date') ? <TableColumnHeaderFilter columnKey="due_date" label={p('col_due_date')} value={filters.due_date || ''} onChange={(v) => setFilter('due_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('form_threshold')}</th>
                  {showCol('priority') ? <TableColumnHeaderFilter columnKey="priority" label={p('col_priority')} kind="select" options={PRIORITY_OPTIONS} value={filters.priority || ''} onChange={(v) => setFilter('priority', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <TableColumnHeaderFilter columnKey="client_name" label={p('col_client')} value={filters.client_name || ''} onChange={(v) => setFilter('client_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={STATUS_OPTIONS} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredAlertes.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {showCol('alert_number') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.alert_number || '—'}</span>
                      </td>
                      ) : null}
                      {showCol('title') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">{record.title || '—'}</span>
                      </td>
                      ) : null}
                      {showCol('alert_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{getAlertTypeLabel(record.alert_type)}</span>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{getDocumentTypeLabel(record.document_type)}</span>
                      </td>
                      {showCol('due_date') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{formatDate(record.due_date)}</span>
                      </td>
                      ) : null}
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">{getDueBadge(record)}</div>
                      </td>
                      {showCol('priority') ? (
                      <td className="px-6 py-4 text-center">{getPriorityBadge(record.priority)}</td>
                      ) : null}
                      <td className="px-6 py-4">
                        <span className="text-white/80">{record.client_name || '—'}</span>
                      </td>
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12 text-white/60">
                      <AlertTriangle size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_total', { count: filteredAlertes.length })}</span>
                      <span>
                        {p('footer_active')}:{' '}
                        <span className="text-white font-semibold">
                          {filteredAlertes.filter((a) => a.status === 'actif').length}
                        </span>
                      </span>
                      <span>
                        {p('footer_near')}:{' '}
                        <span className="text-orange-300 font-semibold">{alertesFooter.proches}</span>
                      </span>
                      <span>
                        {p('footer_late')}:{' '}
                        <span className="text-red-300 font-semibold">{alertesFooter.retards}</span>
                      </span>
                      <span>
                        {p('footer_resolved')}:{' '}
                        <span className="text-blue-300 font-semibold">
                          {filteredAlertes.filter((a) => a.status === 'resolu').length}
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredAlertes.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <AlertTriangle size={20} className="text-orange-400 shrink-0" />
                          {record.title || '—'}
                        </h3>
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        {getPriorityBadge(record.priority)}
                        {getDueBadge(record)}
                        {record.alert_number && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {record.alert_number}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2 flex items-center gap-2">
                            <Bell size={16} className="text-orange-400" />
                            {p('section_regle')}
                          </h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('field_number')}</span> {record.alert_number || '—'}</div>
                            <div><span className="font-medium">{p('field_title')}</span> {record.title || '—'}</div>
                            <div><span className="font-medium">{p('field_type')}</span> {getAlertTypeLabel(record.alert_type)}</div>
                            <div><span className="font-medium">{p('field_document')}</span> {getDocumentTypeLabel(record.document_type)}</div>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-white mb-2 flex items-center gap-2">
                            <Target size={16} className="text-blue-400" />
                            {p('section_cible')}
                          </h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Users size={16} className="text-green-400" />
                              <span className="font-medium">{p('field_client')}</span>{' '}
                              <span className="text-white">{record.client_name || '—'}</span>
                            </div>
                            {record.client_id && (
                              <div><span className="font-medium">{p('field_client_id')}</span> {record.client_id}</div>
                            )}
                            <div><span className="font-medium">{p('field_due')}</span> {formatDate(record.due_date)}</div>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-white mb-2 flex items-center gap-2">
                            <Settings size={16} className="text-yellow-400" />
                            {p('section_parametres')}
                          </h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-orange-500/10 rounded border border-orange-500/30">
                              <span className="font-medium text-orange-300 text-xs">{p('threshold_label')}</span>
                              <p className="text-white font-bold text-lg mt-1">{record.threshold_days ?? 7}</p>
                            </div>
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('field_priority')}</span>
                              <p className="text-white font-semibold mt-1">{priorityLabels[record.priority] || record.priority || '—'}</p>
                            </div>
                            {record.created_at && (
                              <div className="pt-2 border-t border-white/20 text-xs">
                                {p('field_created')} {formatDate(record.created_at)}
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-white mb-2 flex items-center gap-2">
                            <FileText size={16} className="text-red-400" />
                            {p('section_notes')}
                          </h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.notes ? (
                              <div className="mt-2 p-2 bg-white/5 rounded">
                                <p className="text-white/60 text-xs">{record.notes}</p>
                              </div>
                            ) : (
                              <p className="text-white/40 text-xs italic">{p('no_notes')}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <AlertTriangle size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={AlertTriangle}
          formId="alerte-form"
          saveLabel={editingRecord ? p('save') : p('create')}
          onSubmit
          testId="alerte-crud-modal"
          tabs={alertFormTabs}
          activeTab={alertFormTab}
          onTabChange={setAlertFormTab}
        >
                <form id="alerte-form" onSubmit={handleSubmit}>
                {alertFormTab === 'rule' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_alert_number')}</label>
                              <input
                                type="text"
                                value={formData.alert_number}
                                onChange={(e) => setFormData({ ...formData, alert_number: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_alert_number')}
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_title')}</label>
                              <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_title')}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_alert_type')}</label>
                              <CustomSelect
                                value={formData.alert_type}
                                onChange={(val) => setFormData({ ...formData, alert_type: val })}
                                options={ALERT_TYPE_OPTIONS}
                                placeholder={p('placeholder_type_or_select')}
                                searchable={true}
                                creatable={true}
                                autoSelect={false}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_document_type')}</label>
                              <CustomSelect
                                value={formData.document_type}
                                onChange={(val) => setFormData({ ...formData, document_type: val })}
                                options={DOCUMENT_TYPE_OPTIONS}
                                placeholder={p('placeholder_type_or_select')}
                                searchable={true}
                                creatable={true}
                                autoSelect={false}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_status')}</label>
                              <CustomSelect
                                value={formData.status}
                                onChange={(val) => setFormData({ ...formData, status: val })}
                                options={STATUS_OPTIONS}
                                placeholder={p('placeholder_type_or_select')}
                                searchable={true}
                                creatable={true}
                                autoSelect={false}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_client')}</label>
                              <CustomSelect
                                value={formData.client_id}
                                onChange={handleClientChange}
                                options={clientOptions}
                                placeholder={p('placeholder_select_client')}
                                searchable={true}
                                creatable={true}
                                autoSelect={false}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_client_name')}</label>
                              <input
                                type="text"
                                value={formData.client_name}
                                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_client_name')}
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_due_date')}</label>
                              <DatePicker
                                selected={formData.due_date ? new Date(formData.due_date) : null}
                                onChange={(date) =>
                                  setFormData({
                                    ...formData,
                                    due_date: date ? date.toISOString().split('T')[0] : '',
                                  })
                                }
                                dateFormat="dd/MM/yyyy"
                                placeholderText={p('select')}
                                locale={datePickerLocale}
                                renderCustomHeader={renderDatePickerHeader}
                                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 gps-datepicker"
                                calendarClassName="gps-calendar"
                                wrapperClassName="w-full"
                                portalId="qawaem-datepicker-portal"
                                popperClassName="date-picker-popper-high-z"
                              />
                            </div>
                          </div>
                )}

                {alertFormTab === 'params' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_threshold')}</label>
                              <CustomNumberInput
                                step={1}
                                value={formData.threshold_days}
                                onChange={(v) => setFormData({ ...formData, threshold_days: v })}
                                placeholder="7"
                              />
                            </div>
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_priority')}</label>
                              <CustomSelect
                                value={formData.priority}
                                onChange={(val) => setFormData({ ...formData, priority: val })}
                                options={PRIORITY_OPTIONS}
                                placeholder={p('placeholder_type_or_select')}
                                searchable={true}
                                creatable={true}
                                autoSelect={false}
                              />
                            </div>
                            <div className="md:col-span-3">
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_notes')}</label>
                              <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={4}
                                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                placeholder={p('placeholder_notes')}
                              />
                            </div>
                          </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('details_title', { title: viewingRecord.title })}
          icon={AlertTriangle}
          maxWidth="1000px"
          testId="alerte-view-modal"
        >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <Bell size={18} className="text-orange-400" />
                      {p('section_regle')}
                    </h5>
                    <div className="space-y-2 text-sm text-white/70">
                      <div><span className="font-medium">{p('field_alert_number')}</span> {viewingRecord.alert_number || '—'}</div>
                      <div><span className="font-medium">{p('field_title')}</span> {viewingRecord.title || '—'}</div>
                      <div><span className="font-medium">{p('field_type')}</span> {getAlertTypeLabel(viewingRecord.alert_type)}</div>
                      <div><span className="font-medium">{p('field_document')}</span> {getDocumentTypeLabel(viewingRecord.document_type)}</div>
                      <div><span className="font-medium">{p('field_status')}</span> <QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <Target size={18} className="text-blue-400" />
                      {p('section_cible')}
                    </h5>
                    <div className="space-y-2 text-sm text-white/70">
                      <div><span className="font-medium">{p('field_client')}</span> {viewingRecord.client_name || '—'}</div>
                      {viewingRecord.client_id && (
                        <div><span className="font-medium">{p('field_client_id')}</span> {viewingRecord.client_id}</div>
                      )}
                      <div><span className="font-medium">{p('field_due')}</span> {formatDate(viewingRecord.due_date)}</div>
                      <div className="pt-2">{getDueBadge(viewingRecord)}</div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <Settings size={18} className="text-yellow-400" />
                      {p('section_parametres')}
                    </h5>
                    <div className="space-y-2 text-sm text-white/70">
                      <div><span className="font-medium">{p('threshold_label')}</span> {viewingRecord.threshold_days ?? 7}</div>
                      <div><span className="font-medium">{p('field_priority')}</span> {getPriorityBadge(viewingRecord.priority)}</div>
                      <div><span className="font-medium">{p('field_created')}</span> {formatDate(viewingRecord.created_at)}</div>
                      <div><span className="font-medium">{p('field_updated')}</span> {formatDate(viewingRecord.updated_at)}</div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20">
                    <h5 className="font-medium text-white mb-3 flex items-center gap-2">
                      <FileText size={18} className="text-red-400" />
                      {p('section_notes')}
                    </h5>
                    <p className="text-sm text-white/70">{viewingRecord.notes || p('no_notes')}</p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleEdit(viewingRecord);
                    }}
                    className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-200 rounded-xl transition-all flex items-center gap-2"
                  >
                    <Edit size={16} />
                    {p('edit')}
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all"
                  >
                    {t('common.close')}
                  </button>
                </div>
        </QawaemErpViewModal>
      ) : null}
    </div>
  );
};

export default AlertesEcheancesPage;

